/**
 * Media URL helpers — cache busting and user-facing errors.
 * Used by profile/org display paths; never talks to R2.
 */

/**
 * @param {string | null | undefined} url
 * @returns {string}
 */
export function stripMediaCacheBust(url) {
  const raw = String(url || "").trim();
  if (!raw || raw.startsWith("data:")) return raw;
  try {
    const u = new URL(raw);
    u.searchParams.delete("v");
    return u.toString();
  } catch {
    return raw.split("?")[0] || raw;
  }
}

/**
 * Append ?v=<version> so CDNs / browsers fetch the latest bytes.
 * Prefer photoUpdatedAt / logoUpdatedAt (ms). No-ops when version is missing
 * (keeps any existing ?v= on the URL).
 * @param {string | null | undefined} url
 * @param {number | string | null | undefined} versionMs
 * @returns {string | null}
 */
export function withMediaCacheBust(url, versionMs) {
  if (url == null || url === "") return url ?? null;
  const raw = String(url).trim();
  if (!raw || raw.startsWith("data:")) return raw;
  if (!/^https?:\/\//i.test(raw)) return raw;
  if (versionMs == null || !Number.isFinite(Number(versionMs))) {
    return raw;
  }
  const v = Math.trunc(Number(versionMs));
  try {
    const u = new URL(raw);
    u.searchParams.set("v", String(v));
    return u.toString();
  } catch {
    const base = stripMediaCacheBust(raw);
    return `${base}?v=${v}`;
  }
}

/**
 * Map Worker / network errors to short user-facing copy.
 * @param {unknown} err
 * @returns {Error}
 */
export function friendlyMediaError(err) {
  if (!(err instanceof Error) && err && typeof err === "object") {
    const msg = /** @type {{ message?: string }} */ (err).message;
    return friendlyMediaError(new Error(msg || "Upload failed"));
  }
  const base = err instanceof Error ? err : new Error(String(err || "Upload failed"));
  const code = /** @type {{ code?: string, status?: number, name?: string }} */ (base).code;
  const status = /** @type {{ status?: number }} */ (base).status;
  const name = base.name;

  let message = base.message || "Upload failed";
  if (name === "AbortError" || code === "timeout") {
    message = "Upload timed out. Check your connection and try again.";
  } else if (code === "media_api_missing") {
    message = "Photo upload isn’t available right now. Try again later.";
  } else if (code === "auth_required" || code === "auth_invalid" || code === "auth_expired" || status === 401) {
    message = "Your session expired. Sign in again, then retry.";
  } else if (code === "forbidden" || status === 403) {
    message = "You don’t have permission to change this photo.";
  } else if (code === "too_large" || /5\s*MB|too large/i.test(message)) {
    message = "Image must be 5 MB or smaller.";
  } else if (code === "invalid_type" || /JPEG|PNG|WebP/i.test(message)) {
    message = "Please use a JPEG, PNG, or WebP image.";
  } else if (!navigator.onLine || code === "network" || /Failed to fetch|NetworkError/i.test(message)) {
    message = "Network error. Check your connection and try again.";
  } else if (/Firestore|could not save|update failed/i.test(message)) {
    message = "Photo uploaded but couldn’t be saved. Please try again.";
  }

  const out = new Error(message);
  out.code = code || base.code;
  out.status = status;
  return out;
}
